import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { usePoll } from '../../hooks/usePoll';

// ---------------------------------------------------------------------------
// Always-on-top "most active tokens" ticker, fixed to the bottom of every
// page (mounted once in App.js).
//
// Data: /hootdex/most-active - tokens ranked by trailing-24h Hootdex traded
// volume, with last fill price and 24h change. Until that endpoint is
// deployed (or if it fails) the ticker falls back to the latest 100 fills
// from /crypto/marketTrade, so it is never empty while trading is live.
//
// It never covers content: App.js renders a spacer of the same height
// (TICKER_HEIGHT) after the footer, so the last rows/footer can always be
// scrolled clear of it. Hover pauses it; reduced-motion users get a static,
// horizontally scrollable strip instead.
// ---------------------------------------------------------------------------

export const TICKER_HEIGHT = 40;
const LIMIT = 30;
const POLL_MS = 15000;

const scroll = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

const flashUp = keyframes`
  0%   { background: rgba(16, 185, 129, 0.35); }
  100% { background: transparent; }
`;
const flashDown = keyframes`
  0%   { background: rgba(249, 60, 60, 0.35); }
  100% { background: transparent; }
`;

const formatPrice = (p) => {
  const n = Number(p);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(4);
  return n.toPrecision(4);
};

// Fallback when /most-active isn't available: rank the latest fills.
const fromRecentTrades = (trades) => {
  const by = {};
  // marketTrade is newest-first.
  for (const t of Array.isArray(trades) ? trades : []) {
    const price = Number(t.price);
    if (!t.symbol || !(price > 0)) continue;
    const row = (by[t.symbol] ||= {
      symbol: t.symbol,
      price, // newest
      oldest: price,
      volume24h: 0,
      trades: 0,
      path: null
    });
    row.oldest = price;
    row.volume24h += Math.abs(Number(t.amount) || 0) * price;
    row.trades += 1;
  }
  return Object.values(by)
    .map((r) => ({
      ...r,
      changePct:
        r.trades > 1 && r.oldest > 0
          ? ((r.price - r.oldest) / r.oldest) * 100
          : null
    }))
    .sort((a, b) => b.volume24h - a.volume24h)
    .slice(0, LIMIT);
};

function TickerItem({ item, flash }) {
  const change = Number(item.changePct);
  const hasChange = item.changePct !== null && Number.isFinite(change);
  // Direction: this tick's move if the price just changed, else 24h change.
  const dir = flash || (hasChange ? (change >= 0 ? 'up' : 'down') : null);
  const color = dir === 'up' ? '#10B981' : dir === 'down' ? '#F93C3C' : '#9ca3af';

  const content = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        height: TICKER_HEIGHT - 12,
        px: '14px',
        mx: '2px',
        borderRadius: '6px',
        whiteSpace: 'nowrap',
        animation: flash
          ? `${flash === 'up' ? flashUp : flashDown} 1.2s ease-out`
          : 'none'
      }}
    >
      <Box component="span" sx={{ color: '#fff', fontWeight: 600, fontSize: '13px' }}>
        {item.symbol}
      </Box>
      <Box
        component="span"
        sx={{ color: '#d1d5db', fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}
      >
        ${formatPrice(item.price)}
      </Box>
      <Box
        component="span"
        sx={{
          color,
          fontSize: '12px',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {dir === 'up' ? '▲' : dir === 'down' ? '▼' : '•'}
        {hasChange ? ` ${Math.abs(change).toFixed(2)}%` : ''}
      </Box>
    </Box>
  );

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRight: '1px solid #142420'
      }}
    >
      {item.path ? (
        <Link
          to={item.path}
          title={`${item.symbol} — open token page`}
          style={{ textDecoration: 'none' }}
        >
          {content}
        </Link>
      ) : (
        content
      )}
    </Box>
  );
}

export default function MostActiveTicker() {
  const [items, setItems] = useState([]);
  const [flashes, setFlashes] = useState({});
  const prevPrices = useRef({});
  const flashTimer = useRef(null);

  const fetchItems = () =>
    api
      .get('/hootdex/most-active', { params: { limit: LIMIT } })
      .then((res) => {
        const tokens = res.data?.tokens;
        if (!Array.isArray(tokens) || tokens.length === 0) {
          throw new Error('empty');
        }
        return tokens;
      })
      .catch(() =>
        api.get('/crypto/marketTrade').then((res) => fromRecentTrades(res.data))
      )
      .then((next) => {
        if (!Array.isArray(next) || next.length === 0) return; // keep last good
        const f = {};
        for (const t of next) {
          const prev = prevPrices.current[t.symbol];
          if (prev !== undefined && prev !== t.price) {
            f[t.symbol] = t.price > prev ? 'up' : 'down';
          }
        }
        prevPrices.current = Object.fromEntries(next.map((t) => [t.symbol, t.price]));
        setItems(next);
        setFlashes(f);
        clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setFlashes({}), 1500);
      })
      .catch(() => {});

  usePoll(() => fetchItems(), POLL_MS, []);
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  // ~3.5s per item keeps reading speed constant however many tokens there are.
  const duration = useMemo(() => Math.max(30, items.length * 3.5), [items.length]);

  return (
    <Box
      role="marquee"
      aria-label="Most active tokens"
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        // Above page content and sticky headers, below MUI dialogs (1300).
        zIndex: 1250,
        height: TICKER_HEIGHT,
        pb: 'env(safe-area-inset-bottom)',
        display: 'flex',
        alignItems: 'stretch',
        background: 'rgba(20, 21, 24, 0.96)',
        backdropFilter: 'blur(6px)',
        borderTop: '1px solid #142420',
        boxShadow: '0 -6px 18px rgba(0,0,0,0.35)'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: { xs: '10px', sm: '14px' },
          flexShrink: 0,
          gap: '6px',
          background: 'rgba(16, 185, 129, 0.12)',
          borderRight: '1px solid #142420',
          color: '#10B981',
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.8px',
          whiteSpace: 'nowrap'
        }}
      >
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
          MOST ACTIVE
        </Box>
        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
          TOP
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          maskImage:
            'linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)',
          '&:hover .mat-track, &:focus-within .mat-track': {
            animationPlayState: 'paused'
          },
          '@media (prefers-reduced-motion: reduce)': {
            overflowX: 'auto',
            '& .mat-track': { animation: 'none' },
            '& .mat-copy': { display: 'none' }
          }
        }}
      >
        {items.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              px: 2,
              color: '#9ca3af',
              fontSize: '12px'
            }}
          >
            Loading market activity…
          </Box>
        ) : (
          <Box
            className="mat-track"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              height: '100%',
              width: 'max-content',
              animation: `${scroll} ${duration}s linear infinite`
            }}
          >
            {items.map((t) => (
              <TickerItem key={t.symbol} item={t} flash={flashes[t.symbol]} />
            ))}
            {/* Duplicate run for a seamless loop at -50%. */}
            <Box
              component="span"
              className="mat-copy"
              aria-hidden="true"
              sx={{ display: 'inline-flex', alignItems: 'center' }}
            >
              {items.map((t) => (
                <TickerItem
                  key={`${t.symbol}-copy`}
                  item={t}
                  flash={flashes[t.symbol]}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
